import { useEffect } from 'react'
import { getToken, deleteToken, onMessage } from 'firebase/messaging'
import { messaging, messagingPromise } from '../firebase'
import { registerFcmToken } from '../api'
import config from '../config'

const VAPID_KEY = config.firebase.vapidKey
const SW_SCOPE = '/firebase-cloud-messaging-push-scope/'
let isTokenRequestInProgress = false
let lastRegisteredKey = null
const TOKEN_SYNC_INTERVAL = 30 * 60 * 1000

const buildSwUrl = () => {
	const {
		apiKey,
		authDomain,
		projectId,
		storageBucket,
		messagingSenderId,
		appId,
		measurementId
	} = config.firebase.web
	const params = new URLSearchParams({
		env: config.env,
		apiKey,
		authDomain,
		projectId,
		storageBucket,
		messagingSenderId,
		appId
	})
	if (measurementId) params.set('measurementId', measurementId)
	return `/firebase-messaging-sw.js?${params.toString()}`
}

const getRegistrationWorker = registration =>
	registration.active || registration.waiting || registration.installing

const isFirebaseMessagingSw = registration => {
	const worker = getRegistrationWorker(registration)
	return !!worker && worker.scriptURL.includes('firebase-messaging-sw.js')
}


const unregisterStaleFirebaseSw = async expectedSwUrl => {
	if (!('serviceWorker' in navigator) || !navigator.serviceWorker.getRegistrations) return false

	const expectedHref = new URL(expectedSwUrl, window.location.origin).href
	const registrations = await navigator.serviceWorker.getRegistrations()

	const staleRegistrations = registrations
		.filter(isFirebaseMessagingSw)
		.filter(registration => getRegistrationWorker(registration).scriptURL !== expectedHref)

	await Promise.all(
		staleRegistrations.map(async registration => {
			console.log(
				'[FCM] Unregistering stale service worker:',
				getRegistrationWorker(registration).scriptURL
			)
			await registration.unregister()
		})
	)

	return staleRegistrations.length > 0
}

const waitForActive = (registration, timeout = 10000) =>
	new Promise((resolve, reject) => {
		if (registration.active && registration.active.state === 'activated' && registration.pushManager) {
			resolve(registration)
			return
		}

		const worker = registration.installing || registration.waiting

		if (!worker) {
			reject(new Error('Service worker is not ready.'))
			return
		}

		const timeoutId = setTimeout(() => {
			reject(new Error('Service worker activation timed out.'))
		}, timeout)

		worker.addEventListener('statechange', () => {
			if (worker.state === 'activated' && registration.pushManager) {
				clearTimeout(timeoutId)
				resolve(registration)
			}

			if (worker.state === 'redundant') {
				clearTimeout(timeoutId)
				reject(new Error('Service worker became redundant.'))
			}
		})
	})

const useFirebaseNotifications = ({ onForegroundMessage, enabled = true, authKey = null } = {}) => {
	useEffect(() => {
		if (!enabled) {
			// Al cerrar sesión eliminamos el token para dejar de recibir push y forzamos
			// el re-registro en el próximo login.
			lastRegisteredKey = null
			messagingPromise.then(messaging => {
				if (!messaging) return
				deleteToken(messaging).catch(e =>
					console.warn('[FCM] Could not delete token on logout:', e.message)
				)
			})
			return
		}

		const requestToken = async () => {
			if (isTokenRequestInProgress) return
			if (!navigator.onLine) return

			isTokenRequestInProgress = true

			if (
				!('Notification' in window) ||
				!window.isSecureContext ||
				!('serviceWorker' in navigator) ||
				!('PushManager' in window)
			) {
				isTokenRequestInProgress = false
				return
			}

			let permission = Notification.permission
			if (permission === 'default') permission = await Notification.requestPermission()
			if (permission !== 'granted') {
				isTokenRequestInProgress = false
				return
			}

			try {
				const messaging = await messagingPromise
				if (!messaging) {
					isTokenRequestInProgress = false
					return
				}
				const swUrl = buildSwUrl()
				const staleSwRemoved = await unregisterStaleFirebaseSw(swUrl)
				const reg = await navigator.serviceWorker.register(swUrl, { scope: SW_SCOPE })
				const activeReg = await waitForActive(reg)
				// getToken primero: asocia el serviceWorkerRegistration correcto a la instancia
				// de messaging. Si no, deleteToken registraría el SW por defecto (sin config).
				let token = await getToken(messaging, {
					vapidKey: VAPID_KEY,
					serviceWorkerRegistration: activeReg
				})
				// Si se eliminó un SW obsoleto, el token cacheado pudo quedar atado a una
				// suscripción muerta: forzamos su regeneración una sola vez.
				if (token && staleSwRemoved) {
					try {
						await deleteToken(messaging)
						lastRegisteredKey = null
						token = await getToken(messaging, {
							vapidKey: VAPID_KEY,
							serviceWorkerRegistration: activeReg
						})
					} catch (e) {
						console.warn('[FCM] Could not refresh stale token:', e.message)
					}
				}
				// Clave por usuario + token: re-registra si cambia el usuario (re-login)
				// o si Firebase rotó el token.
				const registrationKey = `${authKey || ''}::${token}`
				if (token && registrationKey !== lastRegisteredKey) {
					try {
						await registerFcmToken(token)
						// Solo marcamos como registrado si el POST al backend fue exitoso.
						lastRegisteredKey = registrationKey
					} catch (e) {
						console.warn('Failed to register FCM token on server:', e.message)
					}
				}
			} catch (err) {
				console.log('FCM setup error:', err)
			} finally {
				isTokenRequestInProgress = false
			}
		}

		const onFirstClick = () => {
			if (Notification.permission === 'default') requestToken()
		}

		// Vuelve a chequear el token cuando la pestaña recupera foco (por si Firebase lo rotó).
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') requestToken()
		}

		const onOnline = () => requestToken()

		requestToken()
		window.addEventListener('click', onFirstClick, { once: true })
		window.addEventListener('online', onOnline)
		document.addEventListener('visibilitychange', onVisibilityChange)

		const syncIntervalId = setInterval(requestToken, TOKEN_SYNC_INTERVAL)

		let unsubscribe = () => {}
		messagingPromise.then(messaging => {
			if (!messaging) return
			unsubscribe = onMessage(messaging, payload => {
				console.log('[FCM] Foreground message received:', payload)
				if (typeof onForegroundMessage === 'function') {
					return onForegroundMessage(payload)
				}

				// Muestra la notificación visual en primer plano
				if (payload && payload.notification) {
					const { title, body } = payload.notification

					// Verificamos permiso de nuevo por seguridad
					if (Notification.permission === 'granted') {
						new Notification(title, {
							body: body,
							icon: '/logo192.png' // Asegúrate de que esta ruta sea válida en tu carpeta public
						})
					}
				}
			})
		})

		return () => {
			window.removeEventListener('click', onFirstClick)
			window.removeEventListener('online', onOnline)
			document.removeEventListener('visibilitychange', onVisibilityChange)
			clearInterval(syncIntervalId)
			unsubscribe()
		}
	}, [enabled, onForegroundMessage, authKey])
}

export default useFirebaseNotifications
